import { useLayoutEffect, useRef, type TextareaHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows' | 'value'> & {
  value: string
  /** Vanaf hier schuift het veld in plaats van door te groeien. */
  maxRows?: number
}

/**
 * Een tekstveld dat begint als een regel en met de inhoud meegroeit. Voor korte
 * berichten waar een blok van vier lege regels overdreven is, maar afkappen op
 * een regel het teruglezen onmogelijk maakt.
 *
 * De hoogte wordt in een layout-effect gezet zodat de sprong niet zichtbaar is,
 * en eerst teruggezet naar auto: zonder die reset kan scrollHeight alleen groeien.
 */
export function AutoGrowTextarea({ value, maxRows = 8, className, ...rest }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: value stuurt de hermeting aan; zonder die dep groeit het veld niet mee met de inhoud
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    const line = parseFloat(getComputedStyle(el).lineHeight) || 20
    const chrome = el.offsetHeight - el.clientHeight
    el.style.height = `${Math.min(el.scrollHeight, line * maxRows) + chrome}px`
  }, [value, maxRows])

  return <textarea ref={ref} value={value} rows={1} className={cn('resize-none', className)} {...rest} />
}
